
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { BookOpen } from "lucide-react";
import { getCoursePlayerUrl } from "@/utils/routePaths";

interface ContinueLearningCardProps {
  course: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    profiles: {
      username: string | null;
      full_name: string | null;
      avatar_url: string | null;
    } | null;
  };
  progress: number;
  lastLesson: string;
  lessonId: string;
}

const ContinueLearningCard = ({ course, progress, lastLesson, lessonId }: ContinueLearningCardProps) => {
  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>Continue Learning</CardTitle>
        <CardDescription>
          You're {progress}% through '{course.title}'
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="w-full md:w-1/3 aspect-video rounded-md overflow-hidden">
            {course.thumbnail_url ? (
              <img 
                src={course.thumbnail_url} 
                alt={course.title}
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <BookOpen className="h-10 w-10 text-muted-foreground" />
              </div>
            )}
          </div>
          
          <div className="w-full md:w-2/3 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{course.title}</h3>
              
              {/* Creator info */}
              {course.profiles && (
                <div className="flex items-center mt-1 text-sm text-muted-foreground">
                  <Avatar className="h-5 w-5 mr-2">
                    <AvatarImage 
                      src={course.profiles.avatar_url || undefined} 
                      alt={course.profiles.full_name || 'Creator avatar'}
                    />
                    <AvatarFallback>
                      {course.profiles.full_name?.charAt(0) || 
                       course.profiles.username?.charAt(0) || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <span>
                    {course.profiles.full_name || 
                     course.profiles.username || 'Creator'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Last viewed: {lastLesson}</p>
              <Link to={`${getCoursePlayerUrl(course.id)}?lessonId=${lessonId}`}>
                <Button>
                  Continue Learning
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContinueLearningCard;
